import type React from "react";
import { getDevice } from "./device";
import type { StreamItem } from "../../types/meeting.types";
import { useMeetingStore } from "../../store/meeting.store";

interface Props {
  producerId: string;
  recvTransport: any;
  consumedProducerIdsRef: any;
  socket: any;
  consumersRef: any;
  setStreams: React.Dispatch<React.SetStateAction<StreamItem[]>>;
  audioElementsRef: any;
  mediaStatesRef: any;
}

export async function consumeProducer({
  producerId,
  recvTransport,
  consumedProducerIdsRef,
  socket,
  consumersRef,
  setStreams,
  audioElementsRef,
  mediaStatesRef,
}: Props) {
  if (consumedProducerIdsRef.current.has(producerId)) return;

  consumedProducerIdsRef.current.add(producerId);

  socket.emit(
    "consume",
    {
      producerId,
      rtpCapabilities: getDevice().rtpCapabilities,
    },
    async ({
      id,
      kind,
      rtpParameters,
      userId,
      name,
      role,
      label,
      state: producerState,
    }: any) => {
      const state = {
        ...(mediaStatesRef.current?.[producerId] || {}),
        ...(producerState || {}),
      };

      mediaStatesRef.current = {
        ...mediaStatesRef.current,
        [producerId]: state,
      };

      const consumer = await recvTransport.consume({
        id,
        producerId,
        kind,
        rtpParameters,
      });

      consumersRef.current.set(producerId, consumer);

      const stream = new MediaStream();
      stream.addTrack(consumer.track);
      // console.log("Consumed track", { producerId, kind, userId, name, role });
      const localUser = useMeetingStore.getState().userData as any;
      const localUserIds = [
        localUser?._id,
        localUser?.id,
        localUser?.userId,
      ].map((value) => String(value || ""));
      const isOwnProducer = localUserIds.includes(String(userId || ""));
      const isAdvocate = role === "Advocate";

      if (kind === "audio" && isOwnProducer) {
        consumer.close?.();
        consumersRef.current.delete(producerId);
        consumedProducerIdsRef.current.delete(producerId);
        return;
      }

      /* ================================
         🎥 VIDEO
      ================================= */
      if (kind === "video") {
        setStreams((prev) => {
          const existing = prev.find((s) => s.userId === userId && !s.isLocal);

          if (existing) {
            /* Merge into existing tile */
            return prev.map((s) =>
              s.userId === userId
                ? {
                    ...s,
                    stream,
                    videoProducerId: producerId,
                    audioPaused: s.audioPaused ?? isAdvocate,
                    videoPaused: state.videoPaused ?? s.videoPaused,
                    frozen: state.frozen ?? s.frozen,
                    blurred: state.blurred ?? s.blurred,
                  }
                : s,
            );
          }

          /* Create new tile */
          return [
            ...prev,
            {
              userId,
              producerId,
              stream,
              isLocal: false,
              name: name || role || "Participant",
              role: role || "participant",
              label,

              audioPaused: isAdvocate,
              videoPaused: state.videoPaused,
              frozen: state.frozen,
              blurred: state.blurred,

              videoProducerId: producerId,
              audioProducerId: undefined,
            },
          ];
        });
      } else if (kind === "audio") {
        /* ================================
         🔊 AUDIO
      ================================= */
        const audio = document.createElement("audio");
        audio.srcObject = stream;
        audio.autoplay = true;
        audio.muted = false;
        audio.volume = 1;

        document.body.appendChild(audio);

        audioElementsRef.current.set(producerId, audio);

        /* 🔥 MERGE OR CREATE */
        setStreams((prev) => {
          const existing = prev.find((s) => s.userId === userId && !s.isLocal);

          if (existing) {
            return prev.map((s) =>
              s.userId === userId
                ? {
                    ...s,
                    audioProducerId: producerId,

                    audioPaused: state.audioPaused ?? s.audioPaused,
                    videoPaused: s.videoPaused,
                    frozen: s.frozen,
                    blurred: s.blurred,
                  }
                : s,
            );
          }

          /* Audio arrived first → create placeholder */
          return [
            ...prev,
            {
              userId,
              producerId,
              stream: new MediaStream(), // placeholder
              audioPaused: state.audioPaused ?? false,
              videoPaused: state.videoPaused ?? false,
              frozen: state.frozen ?? false,
              blurred: state.blurred ?? false,
              isLocal: false,
              name: name || role || "Participant",
              role: role || "participant",
              label,

              audioProducerId: producerId,
              videoProducerId: undefined,
            },
          ];
        });

        /* Cleanup */
        consumer.on("producerclose", () => {
          audio.remove();
          audioElementsRef.current.delete(producerId);
        });

        consumer.on("transportclose", () => {
          audio.remove();
          audioElementsRef.current.delete(producerId);
        });
      }
    },
  );
}
